import { useEffect, useRef, useCallback } from 'react';

const IDLE_TIMEOUT   = 10 * 60 * 1000; // 10 minutes
const WARN_BEFORE_MS = 60 * 1000;       // warn 1 minute before logout

/**
 * Calls onLogout() after IDLE_TIMEOUT of no user activity.
 * Shows a warning toast 1 minute before logout via onWarn(secondsLeft).
 * Any activity resets the timer and clears the warning.
 * Pass null for onLogout to disable the hook (e.g. when user is not logged in).
 */
export default function useIdleLogout(onLogout, onWarn, onWarnClear) {
  const logoutTimer = useRef(null);
  const warnTimer   = useRef(null);

  const clearTimers = useCallback(() => {
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (warnTimer.current)   clearTimeout(warnTimer.current);
  }, []);

  const resetTimer = useCallback(() => {
    if (!onLogout) return; // disabled — user not logged in
    clearTimers();
    onWarnClear?.();

    warnTimer.current = setTimeout(() => {
      onWarn?.();
    }, IDLE_TIMEOUT - WARN_BEFORE_MS);

    logoutTimer.current = setTimeout(() => {
      if (typeof onLogout === 'function') onLogout();
    }, IDLE_TIMEOUT);
  }, [onLogout, onWarn, onWarnClear, clearTimers]);

  useEffect(() => {
    if (!onLogout) {
      clearTimers(); // clear any running timers if user logged out
      return;
    }
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer(); // start on mount

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimers();
    };
  }, [onLogout, resetTimer, clearTimers]);
}
